const { renderSafeAnswer } = require('./safe-render');
const {
    MAX_COMPLETION_TOKENS,
    MAX_EMERGENCY_COMPACT_CHARACTERS,
    MAX_REQUEST_BODY_BYTES,
    MAX_RESPONSE_CONTINUATIONS,
    buildContinuationMessages,
    buildCompactConversationMessages,
    buildConversationMessages,
    fitRequestToBodyLimit,
    getResponseParts,
    getUtf8ByteLength,
    mergeResponseParts,
} = require('./chat-utils');
//DOM Elementes needed for this to work
var chatBox = document.getElementById('displayMessages');
var chatInput = document.getElementById('userInput');
var sendButton = document.getElementById('sendButton');
//API Key
var apiKey = process.env.CHATBOT_API_KEY || '';
var chatHistory = [];
const systemPrompt =
    'Only answer the last question the user asks. Do not mention conversation history. Reply with only your response and do not add prefixes. When asked to generate code reply with "I am not intended to generate code, for that go to AI Code.", and NEVER GENERATE CODE. Try to respond with emojis sometimes.';

function createThinkingBlock(reasoning) {
    const details = document.createElement('details');
    details.className = 'message-thinking';

    const summary = document.createElement('summary');
    summary.textContent = 'Thinking process';

    const content = document.createElement('div');
    content.className = 'message-thinking-content';
    content.textContent = reasoning;

    details.append(summary, content);
    return details;
}

function appendUserMessage(userMessage) {
    const message = document.createElement('div');
    message.className = 'message-user';
    message.textContent = userMessage;
    chatBox.appendChild(message);
}

function appendAssistantPlaceholder() {
    const message = document.createElement('div');
    message.className = 'message-ai';

    const sphere = document.createElement('div');
    sphere.className = 'sphere';
    message.appendChild(sphere);
    chatBox.appendChild(message);
}

//Get Response from AI Model
async function getResponse(userMessage) {
    //API URL
    const apiUrl = 'https://api.groq.com/openai/v1/chat/completions';
    const aiResponseMessage = chatBox.lastElementChild;

    if (!apiKey) {
        aiResponseMessage.textContent =
            'Chatbot is not configured. Add CHATBOT_API_KEY to the Netlify build environment and redeploy.';
        return;
    }

    chatHistory.push({ role: 'user', content: userMessage });

    try {
        const responseParts = await getCompleteResponse(
            apiUrl,
            buildConversationMessages(systemPrompt, chatHistory),
        );
        const aiResponse = responseParts.answer;

        aiResponseMessage.replaceChildren();
        if (responseParts.reasoning) {
            aiResponseMessage.appendChild(createThinkingBlock(responseParts.reasoning));
        }

        const answerElement = document.createElement('div');
        answerElement.className = 'message-answer';
        aiResponseMessage.appendChild(answerElement);

        let speed = 5;
        if (aiResponse.length > 250) {
            speed = 15;
        } else if (aiResponse.length > 100) {
            speed = 7;
        }

        typeEffect(answerElement, aiResponse, speed, () => {
            if (responseParts.incomplete) {
                appendIncompleteNotice(aiResponseMessage);
            }
        });
        chatHistory.push({ role: 'assistant', content: responseParts.answer });
    } catch (error) {
        //Catch errors and display error message
        aiResponseMessage.textContent = 'Oops! Something went wrong. Try again later!';
        Swal.fire({
            title: "Error!",
            text: "Do you want to continue",
            icon: 'error',
            iconColor: 'black',
            color: 'black',
            confirmButtonText: 'OK',
            confirmButtonColor: 'black',
            timer: 5000,
            timerProgressBar: true
        })
    }
}

async function requestCompletion(apiUrl, messages) {
    const createRequestBody = (requestMessages) => JSON.stringify({
        model: "qwen/qwen3.6-27b",
        messages: requestMessages,
        temperature: 1,
        top_p: 1,
        stream: false,
        reasoning_effort: "default",
        reasoning_format: "parsed",
        max_completion_tokens: MAX_COMPLETION_TOKENS,
    });
    const request = (requestBody) => fetch(apiUrl, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${apiKey}`,
        },
        body: requestBody,
    });
    let { messages: requestMessages, body: requestBody, fits } = fitRequestToBodyLimit(
        messages,
        createRequestBody,
    );
    if (!fits || getUtf8ByteLength(requestBody) > MAX_REQUEST_BODY_BYTES) {
        throw new Error('Unable to fit the chat request within the provider size limit.');
    }

    let response = await request(requestBody);
    if (response.status === 413) {
        const compactMessages = buildCompactConversationMessages(
            requestMessages,
            MAX_EMERGENCY_COMPACT_CHARACTERS,
        );
        const compactBody = createRequestBody(compactMessages);
        if (compactBody !== requestBody) {
            response = await request(compactBody);
        }
    }

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data?.error?.message || `Request failed with status ${response.status}`);
    }

    const choice = data?.choices?.[0];
    if (!choice?.message) {
        throw new Error('The API returned no assistant message.');
    }

    return choice;
}

async function getCompleteResponse(apiUrl, messages) {
    let requestMessages = messages;
    let combined = { answer: '', reasoning: '' };

    for (let attempt = 0; attempt <= MAX_RESPONSE_CONTINUATIONS; attempt++) {
        const choice = await requestCompletion(apiUrl, requestMessages);
        combined = mergeResponseParts(combined, getResponseParts(choice.message));

        if (choice.finish_reason !== 'length' || attempt === MAX_RESPONSE_CONTINUATIONS) {
            return {
                ...combined,
                incomplete: choice.finish_reason === 'length',
            };
        }

        requestMessages = buildContinuationMessages(messages, combined.answer);
    }
}

function typeEffect(element, text, speed, onComplete) {
    element.textContent = "";
    const typingCursor = document.createElement("span");
    typingCursor.classList.add("typing-cursor");
    element.appendChild(typingCursor);
    let index = 0;
    const chunkSize = text.length > 2500 ? 12 : 1;

    function type() {
        if (index < text.length) {
            index = Math.min(index + chunkSize, text.length);
            element.textContent = text.substring(0, index);
            setTimeout(type, speed);
            chatBox.scrollTop = chatBox.scrollHeight;
        } else {
            renderSafeAnswer(element, text, copyCode);
            onComplete();
        }
    }

    type();
}

function appendIncompleteNotice(container) {
    const notice = document.createElement('div');
    notice.className = 'message-truncated';
    notice.textContent = 'The response reached the output limit. Send “continue” to finish it.';
    container.appendChild(notice);
}
//Event Listener for send Button
sendButton.addEventListener('click', function () {
    var userInput = chatInput.value;
    if (userInput === '') {
        return;
    }
    appendUserMessage(userInput);
    appendAssistantPlaceholder();
    //Call the function
    getResponse(userInput)
    chatInput.value = '';
    chatBox.scrollTop = chatBox.scrollHeight;
})
//Event listener for Enter key pressed
chatInput.addEventListener('keypress', function (event) {
    if (event.key == 'Enter') {
        var userInput = chatInput.value;
        if (userInput === '') {
            return;
        }
        appendUserMessage(userInput);
        appendAssistantPlaceholder();
        //Call the function
        getResponse(userInput)
        chatInput.value = '';
        chatBox.scrollTop = chatBox.scrollHeight;
    }
}) 
//Function to copy code from the AI Generated Code
function copyCode(codeContent) {
    if (typeof codeContent === 'string') {
        codeContent = document.getElementById(codeContent);
    }
    if (!codeContent) {
        return;
    }
    window.navigator.clipboard.writeText(codeContent.textContent);
    const Toast = Swal.mixin({
        toast: true,
        position: 'bottom-end',
        showConfirmButton: false,
        timer: 1000,
        timerProgressBar: true,
        didOpen: (toast) => {
            toast.addEventListener('mouseenter', Swal.stopTimer)
            toast.addEventListener('mouseleave', Swal.resumeTimer)
        }
    })
    Toast.fire({
        title: "Copied!",
        customClass: {
            title: 'black'
        },
        icon: 'success',
        iconColor: '#FF642C',
        color: 'black',
    })
}
